import React from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
const CreatorRewards: React.FC = () => {
  return <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-black mb-4">Monetization</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            How iSPAI members earn rewards and income on the platform
          </p>
        </div>

        <div className="space-y-8">
          {/* Knowledge Base Section */}
          <Card className="border-2 border-gray-300 overflow-hidden">
            <CardHeader className="bg-gray-100">
              <CardTitle className="text-2xl font-bold flex items-center">
                <span className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">1</span>
                Knowledge Base (Single-Purpose Content)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Content for grounding (license or sell)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>An engineering expert uploads technical documents on offshore rig safety procedures that can be licensed to oil & gas companies needing AI grounding in this specialized domain.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>70% revenue share paid in USDC on licensing fees when your content is used for grounding AI systems, with options for one-time sales or recurring licensing models based on usage metrics.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Person for guidance (employ or retain)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>A cybersecurity specialist is retained as a domain expert to provide real-time guidance to companies implementing new security protocols with AI assistance.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Set your own consulting rates (hourly/project-based) paid in USDC. Opportunities for both short-term projects and long-term retainer arrangements with enterprise clients.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Solution Section */}
          <Card className="border-2 border-gray-300 overflow-hidden">
            <CardHeader className="bg-gray-100">
              <CardTitle className="text-2xl font-bold flex items-center">
                <span className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">2</span>
                Solution (Multi-Purpose Content)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Cross-domain content for grounding (license or sell)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>A team creates a comprehensive solution that combines construction expertise with environmental compliance knowledge, creating a unique cross-domain knowledge product.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Premium pricing (80% revenue share) paid in USDC for multi-domain solutions with tiered licensing options based on exclusivity and usage scope. Enterprise clients typically pay 2-3x more for cross-domain solutions versus single-domain content.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">People for guidance (employ or retain)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>A multi-disciplinary team of experts in both healthcare and AI is retained to guide a hospital's implementation of AI-powered diagnostic tools, providing both technical and domain expertise.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Team-based consulting paid in USDC with revenue distribution based on pre-agreed splits among team members. Platform fee reduced to 10% for solutions involving 3+ domain experts, with additional performance bonuses for successful implementations.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Giblet+ Section */}
          <Card className="border-2 border-gray-300 overflow-hidden">
            <CardHeader className="bg-gray-100">
              <CardTitle className="text-2xl font-bold flex items-center">
                <span className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">3</span>
                Giblet + (Licensing a member's AI Agent)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Member (Giblet 1)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>A petroleum engineer creates a specialized Giblet trained on their expertise for analyzing well logs and optimizing drilling operations, which companies can license for use without direct consultation.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>60% revenue share paid in USDC on all usage fees of your personal Giblet, with pricing tiers based on usage volume, complexity, and exclusivity. Typical pricing ranges from $50-500 per month per corporate user depending on specialization level.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Community (Giblet 1 + Giblet n)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>A construction management Giblet is created by combining multiple expert Giblets specializing in scheduling, materials science, and regulatory compliance to create a comprehensive solution.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Revenue paid in USDC is distributed among contributing Giblet owners based on usage metrics and contribution weighting. Primary Giblet creator receives 40%, with 45% distributed among contributing Giblets and 15% platform fee. Enterprise licenses typically range from $5,000-25,000/month.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Network Section */}
          <Card className="border-2 border-gray-300 overflow-hidden">
            <CardHeader className="bg-gray-100">
              <CardTitle className="text-2xl font-bold flex items-center">
                <span className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">4</span>
                Network (Staking in teams and communities to earn rewards)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Knowledge Network (Rewards from solution bounties captured)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>By staking tokens in a renewable energy knowledge network, you earn rewards when that network's experts successfully complete solution bounties for renewable energy integration projects.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Earn 5-15% of bounty rewards paid in USDC (MVP phase) or Gibs (future) based on your staking percentage in the knowledge network. Higher stakes and longer staking periods increase your reward percentage, with quarterly dividend distributions based on network performance.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-2">•</span>
                  <HoverCard>
                    <HoverCardTrigger className="cursor-help">
                      <span className="text-lg">Channel Network (Dividends based on the value of the channel)</span>
                    </HoverCardTrigger>
                    <HoverCardContent className="p-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold">Example Scenario:</h4>
                        <p>Stakeholders in the CURT Channel receive dividends proportional to their stake when the channel generates revenue through enterprise subscriptions, solution delivery, or content licensing.</p>
                        <h4 className="font-semibold mt-2">Revenue Structure:</h4>
                        <p>Channel stakeholders receive quarterly dividends paid in USDC (MVP phase) or Gibs (future) based on overall channel performance with additional rewards for channel growth. Early stakeholders receive preferential multipliers (1.5-2x) on standard dividend rates. Typical annual returns range from 8-22% depending on channel growth and activity.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>;
};
export default CreatorRewards;